import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CursorDot from "../components/CursorDot";
import StarterKitFlow from "../components/StarterKitFlow";

const DELIVERABLES = [
  {
    title: "LinkedIn profile optimization",
    desc: "Headline, About section, and Featured section rewritten to signal what you actually do and who it is for.",
  },
  {
    title: "Content pillars mapped to your offer",
    desc: "The themes you should own on LinkedIn, aligned to your product or service across the buyer journey.",
  },
  {
    title: "A personal voice guide",
    desc: "A short document that captures how you sound, so you or any AI can write posts that read like you, not like generic AI content.",
  },
  {
    title: "8 ready to post pieces",
    desc: "Eight posts written in your voice, on your pillars, ready to publish this month.",
  },
  {
    title: "A daily engagement list",
    desc: "A curated list of accounts to engage with each day so the right people start seeing your work.",
  },
];

const STEPS = [
  {
    n: "01",
    title: "Apply",
    body: "Fill the short form below so we can see if it is a fit.",
  },
  {
    n: "02",
    title: "Talk",
    body: "If it looks right, we get on a short call to make sure we can genuinely help you.",
  },
  {
    n: "03",
    title: "Get your kit",
    body: "Once you are in, we build everything and hand it over, ready to use.",
  },
];

const AUDIENCES = [
  "Founders growing a company and their own authority.",
  "Freelancers and consultants who want inbound clients instead of cold outreach.",
  "Experts sitting on knowledge their audience never sees.",
];

function InlineBooking({ visible }) {
  const initialized = useRef(false);

  useEffect(() => {
    if (!visible || initialized.current) return;
    initialized.current = true;

    (function (C, A, L) {
      let p = function (a, ar) {
        a.q.push(ar);
      };
      let d = C.document;
      C.Cal =
        C.Cal ||
        function () {
          let cal = C.Cal;
          let ar = arguments;
          if (!cal.loaded) {
            cal.ns = {};
            cal.q = cal.q || [];
            d.head.appendChild(d.createElement("script")).src = A;
            cal.loaded = true;
          }
          if (ar[0] === L) {
            const api = function () {
              p(api, arguments);
            };
            const namespace = ar[1];
            api.q = api.q || [];
            if (typeof namespace === "string") {
              cal.ns[namespace] = cal.ns[namespace] || api;
              p(cal.ns[namespace], ar);
              p(cal, ["initNamespace", namespace]);
            } else p(cal, ar);
            return;
          }
          p(cal, ar);
        };
    })(window, "https://app.cal.com/embed/embed.js", "init");

    window.Cal("init", "starter30min", { origin: "https://app.cal.com" });

    window.Cal.ns["starter30min"]("inline", {
      elementOrSelector: "#cal-inline-starter",
      config: { layout: "month_view", useSlotsViewOnSmallScreen: "true" },
      calLink: "mb-shelar-a8hrax/30min",
    });

    window.Cal.ns["starter30min"]("ui", {
      cssVarsPerTheme: {
        light: { "cal-brand": "#ff3301" },
        dark: { "cal-brand": "#e4e4e4" },
      },
      hideEventTypeDetails: false,
      layout: "month_view",
    });
  }, [visible]);

  if (!visible) return null;
  return (
    <div
      className="cal-embed-wrap shadow-xl border border-neutral-200/60"
      data-testid="starter-kit-cal-embed"
    >
      <div
        id="cal-inline-starter"
        style={{ width: "100%", height: "100%", overflow: "scroll" }}
      />
    </div>
  );
}

export default function StarterKit() {
  const [flowOpen, setFlowOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const bookingRef = useRef(null);

  const openFlow = () => setFlowOpen(true);
  const closeFlow = () => setFlowOpen(false);

  const handleSuccess = () => {
    setFlowOpen(false);
    setSubmitted(true);
    setTimeout(() => {
      bookingRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 220);
  };

  return (
    <main style={{ background: "#EBEBEB" }} data-testid="starter-kit-page">
      <Helmet>
        <title>Founder Starter Kit — LinkedIn Lab</title>
        <meta
          name="description"
          content="A done-for-you LinkedIn Starter Kit for founders, freelancers, and consultants. Profile, content pillars, voice guide, 8 posts, and a daily engagement list."
        />
        <link
          rel="canonical"
          href="https://thelinkedinlab.in/starter-kit"
        />
        <meta property="og:title" content="Founder Starter Kit — LinkedIn Lab" />
        <meta
          property="og:description"
          content="Done-for-you LinkedIn presence for founders, freelancers, and consultants. Selective intake."
        />
        <meta
          property="og:url"
          content="https://thelinkedinlab.in/starter-kit"
        />
        <meta
          property="og:image"
          content="https://thelinkedinlab.in/og-image.png"
        />
      </Helmet>

      <CursorDot />
      <Navbar />

      {/* HERO */}
      <section className="pt-[160px] pb-20">
        <div className="container-x">
          <div className="max-w-4xl">
            <h1
              className="font-display"
              style={{
                fontSize: "clamp(44px, 6.4vw, 96px)",
                letterSpacing: "-0.045em",
                lineHeight: 1.0,
              }}
              data-testid="starter-kit-hero-heading"
            >
              Your expertise deserves to be seen.{" "}
              <span className="gradient-orange-text">
                We make that happen, without you writing a word.
              </span>
            </h1>
            <p
              className="mt-8 text-neutral-700 leading-relaxed"
              style={{ fontSize: 19, maxWidth: 720 }}
            >
              A done for you LinkedIn Starter Kit for founders, freelancers,
              and consultants who know their craft but do not have time to
              build their presence.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-5">
              <button
                type="button"
                onClick={openFlow}
                data-testid="starter-kit-hero-cta"
                className="inline-flex items-center justify-center px-7 py-4 rounded-full font-medium transition hover:opacity-90"
                style={{
                  background: "#0A0A0A",
                  color: "#fff",
                  fontSize: 16,
                }}
              >
                Apply for the Starter Kit
              </button>
              <span className="text-[14px] text-neutral-600">
                Limited founding client spots. Selective intake.
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="pb-24">
        <div className="container-x">
          <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
            <h2
              className="font-display"
              style={{
                fontSize: "clamp(34px, 4.4vw, 60px)",
                letterSpacing: "-0.035em",
                lineHeight: 1.05,
              }}
            >
              You are great at what you do.{" "}
              <span className="gradient-orange-text">
                Your LinkedIn does not show it.
              </span>
            </h2>
            <p
              className="text-neutral-700 leading-relaxed"
              style={{ fontSize: 18 }}
            >
              Most experts have the knowledge but not the time or the system
              to post consistently, in their own voice, to the people who
              matter. So the profile sits quiet, and the opportunities go to
              louder, less capable people.
            </p>
          </div>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="pb-24">
        <div className="container-x">
          <div className="max-w-3xl mb-14">
            <h2
              className="font-display"
              style={{
                fontSize: "clamp(36px, 4.8vw, 68px)",
                letterSpacing: "-0.04em",
                lineHeight: 1.02,
              }}
            >
              The Founder{" "}
              <span className="gradient-orange-text">Starter Kit</span>
            </h2>
            <p className="mt-5 text-neutral-600" style={{ fontSize: 17 }}>
              One offer. Done once, done properly. Everything you need to show
              up on LinkedIn like the expert you already are.
            </p>
          </div>

          <ol
            className="border-t border-neutral-300/70"
            data-testid="starter-kit-deliverables"
          >
            {DELIVERABLES.map((d, i) => (
              <li
                key={d.title}
                className="grid grid-cols-[auto_1fr] md:grid-cols-[auto_1fr_1.2fr] gap-x-6 md:gap-x-10 gap-y-2 py-8 md:py-10 border-b border-neutral-300/70"
              >
                <div
                  className="font-display"
                  style={{
                    color: "var(--agero-orange)",
                    fontSize: "clamp(30px, 3.4vw, 40px)",
                    letterSpacing: "-0.02em",
                    lineHeight: 1,
                    minWidth: 56,
                  }}
                >
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3
                  className="font-display self-center"
                  style={{
                    fontSize: "clamp(22px, 2.4vw, 30px)",
                    letterSpacing: "-0.025em",
                    lineHeight: 1.1,
                  }}
                >
                  {d.title}
                </h3>
                <p
                  className="text-neutral-700 leading-relaxed col-start-2 md:col-start-3"
                  style={{ fontSize: 16.5, maxWidth: 560 }}
                >
                  {d.desc}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* WHO IT IS FOR */}
      <section className="pb-24">
        <div className="container-x">
          <div className="max-w-3xl">
            <h2
              className="font-display"
              style={{
                fontSize: "clamp(32px, 4.2vw, 56px)",
                letterSpacing: "-0.035em",
                lineHeight: 1.05,
              }}
            >
              Built for people who are genuinely good at what they do.
            </h2>
          </div>
          <ul className="mt-10 max-w-3xl space-y-4">
            {AUDIENCES.map((line) => (
              <li key={line} className="flex items-start gap-4">
                <span
                  className="mt-2 w-2 h-2 rounded-full flex-shrink-0"
                  style={{ background: "var(--agero-orange)" }}
                />
                <span
                  className="text-neutral-800 leading-relaxed"
                  style={{ fontSize: 18 }}
                >
                  {line}
                </span>
              </li>
            ))}
          </ul>
          <p
            className="mt-10 text-neutral-600 max-w-2xl"
            style={{ fontSize: 16 }}
          >
            We take a small number of clients and only work with people who
            have a real point of view.
          </p>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="pb-24">
        <div className="container-x">
          <div className="max-w-3xl mb-12">
            <h2
              className="font-display"
              style={{
                fontSize: "clamp(32px, 4.2vw, 56px)",
                letterSpacing: "-0.035em",
                lineHeight: 1.05,
              }}
            >
              How it works
            </h2>
          </div>
          <div
            className="grid md:grid-cols-3 gap-10 md:gap-6"
            data-testid="starter-kit-steps"
          >
            {STEPS.map((s) => (
              <div key={s.n} className="md:pr-6">
                <div
                  className="font-display"
                  style={{
                    color: "var(--agero-orange)",
                    fontSize: 34,
                    letterSpacing: "-0.02em",
                    lineHeight: 1,
                  }}
                >
                  {s.n}
                </div>
                <h3
                  className="font-display mt-3"
                  style={{
                    fontSize: 24,
                    letterSpacing: "-0.02em",
                    lineHeight: 1.15,
                  }}
                >
                  {s.title}
                </h3>
                <p
                  className="mt-3 text-neutral-700 leading-relaxed"
                  style={{ fontSize: 16 }}
                >
                  {s.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="pb-24">
        <div className="container-x">
          <div className="max-w-3xl">
            <p
              className="text-neutral-800 leading-relaxed"
              style={{ fontSize: 20 }}
            >
              The full value of this is{" "}
              <span
                className="font-display"
                style={{ color: "var(--agero-orange)" }}
              >
                $1500
              </span>
              . Founding clients get it at a reduced rate in exchange for an
              honest testimonial. Spots are limited.
            </p>
          </div>
        </div>
      </section>

      {/* APPLY (secondary CTA) */}
      <section id="apply" className="pb-24">
        <div className="container-x">
          <div className="max-w-3xl">
            <h2
              className="font-display"
              style={{
                fontSize: "clamp(36px, 4.8vw, 64px)",
                letterSpacing: "-0.04em",
                lineHeight: 1.02,
              }}
              data-testid="starter-kit-form-heading"
            >
              {submitted
                ? "Application received."
                : "Ready to apply?"}
            </h2>
            <p className="mt-5 text-neutral-600" style={{ fontSize: 17 }}>
              {submitted
                ? "Got it. We review every application personally. Book a quick call below so we can talk it through."
                : "Takes about 2 minutes. Eleven quick questions, one at a time. We review every application personally."}
            </p>
            {!submitted && (
              <button
                type="button"
                onClick={openFlow}
                data-testid="starter-kit-apply-cta"
                className="mt-8 inline-flex items-center justify-center px-7 py-4 rounded-full font-medium transition hover:opacity-90"
                style={{
                  background: "#0A0A0A",
                  color: "#fff",
                  fontSize: 16,
                }}
              >
                Start the application
              </button>
            )}
          </div>
        </div>
      </section>

      {/* BOOKING (visible only after form success) */}
      <section
        id="book"
        ref={bookingRef}
        className={submitted ? "pb-24" : "pb-0"}
      >
        <div className="container-x">
          {submitted && (
            <div className="max-w-3xl mb-10">
              <h2
                className="font-display"
                style={{
                  fontSize: "clamp(32px, 4.2vw, 56px)",
                  letterSpacing: "-0.035em",
                  lineHeight: 1.05,
                }}
                data-testid="starter-kit-booking-heading"
              >
                Book your call
              </h2>
              <p className="mt-4 text-neutral-600" style={{ fontSize: 16.5 }}>
                Pick a time that works for you. We usually reply with any
                notes on your application right after the call.
              </p>
            </div>
          )}
          <InlineBooking visible={submitted} />
        </div>
      </section>

      <Footer />

      <StarterKitFlow
        open={flowOpen}
        onClose={closeFlow}
        onSuccess={handleSuccess}
      />
    </main>
  );
}
